import { Modal, Stack } from "@mantine/core"
import { InfoBox, Select } from "@tfg/ui"
import type { TFunction } from "i18next"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import type { ResponseConfig } from "~/backend/axiosClient"
import type { DatapointResponseVersionSnapshotDto } from "~/gen/api/types/DatapointResponseVersionSnapshotDto.ts"
import type { ResolveDatapointResponseCanonicalValueResponseDto } from "~/gen/api/types/ResolveDatapointResponseCanonicalValueResponseDto.ts"
import { useAuth } from "~/store/auth-context.tsx"

import { DatapointResponseSnapshotDetails } from "./DatapointResponseSnapshotDetails.tsx"
import type {
  DatapointResponseVersionsModalState,
  DatapointScopeTarget,
} from "./datapoint-response-versions.types.ts"
import {
  resolveAuthorOrganizationLabel,
  shortOrganizationId,
} from "./resolve-author-organization-label.ts"
import { useDatapointCanonicalValueQuery } from "./useDatapointCanonicalValueQuery.ts"

type DatapointResponseVersionsModalProps = {
  readonly opened: boolean
  readonly onClose: () => void
  readonly modalState: DatapointResponseVersionsModalState
}

const getDefaultAuthorOrganizationId = (
  allResponses: readonly DatapointResponseVersionSnapshotDto[],
  canonicalAuthorOrganizationId: string | undefined
): string | null => {
  if (canonicalAuthorOrganizationId) {
    return canonicalAuthorOrganizationId
  }

  return allResponses[0]?.authorOrganizationId ?? null
}

type DatapointResponseVersionsContentProps = {
  readonly responseData: ResolveDatapointResponseCanonicalValueResponseDto
  readonly target: DatapointScopeTarget
  readonly currentOrganizationId: string | undefined
  readonly currentOrganizationName: string | undefined
  readonly t: TFunction<"products">
}

const DatapointResponseVersionsContent = ({
  responseData,
  target,
  currentOrganizationId,
  currentOrganizationName,
  t,
}: DatapointResponseVersionsContentProps) => {
  const allResponses = responseData.allResponses

  const defaultAuthorOrganizationId = getDefaultAuthorOrganizationId(
    allResponses,
    responseData.canonicalValue?.authorOrganizationId
  )

  const [selectedAuthorOrganizationId, setSelectedAuthorOrganizationId] =
    useState<string | null>(defaultAuthorOrganizationId)

  useEffect(() => {
    setSelectedAuthorOrganizationId(defaultAuthorOrganizationId)
  }, [defaultAuthorOrganizationId, target])

  const organizationOptions = useMemo(
    () =>
      allResponses.map((snapshot) => {
        const resolvedLabel = resolveAuthorOrganizationLabel({
          authorOrganizationId: snapshot.authorOrganizationId,
          snapshot,
          currentOrganizationId,
          currentOrganizationName,
        })

        return {
          value: snapshot.authorOrganizationId,
          label:
            resolvedLabel ??
            t("details.responseVersions.unknownOrganization", {
              id: shortOrganizationId(snapshot.authorOrganizationId),
            }),
        }
      }),
    [allResponses, currentOrganizationId, currentOrganizationName, t]
  )

  const activeAuthorOrganizationId =
    selectedAuthorOrganizationId ?? defaultAuthorOrganizationId

  const selectedSnapshot = allResponses.find(
    (snapshot) => snapshot.authorOrganizationId === activeAuthorOrganizationId
  )

  if (allResponses.length === 0) {
    return (
      <InfoBox
        variant="info"
        message={t("details.responseVersions.empty")}
        width="100%"
      />
    )
  }

  return (
    <Stack gap="md">
      <Select
        label={t("details.responseVersions.organization")}
        data={organizationOptions}
        value={activeAuthorOrganizationId}
        onChange={(value) => setSelectedAuthorOrganizationId(value)}
      />

      {selectedSnapshot && (
        <DatapointResponseSnapshotDetails
          snapshot={selectedSnapshot}
          target={target}
          canonicalValue={responseData.canonicalValue}
        />
      )}
    </Stack>
  )
}

export const DatapointResponseVersionsModal = ({
  opened,
  onClose,
  modalState,
}: DatapointResponseVersionsModalProps) => {
  const { t } = useTranslation("products")
  const { user } = useAuth()

  const query = useDatapointCanonicalValueQuery(
    modalState?.target ?? null,
    opened && modalState !== null
  )

  const fieldLabel = modalState?.fieldLabel ?? ""

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("details.responseVersions.title", { field: fieldLabel })}
      size="lg"
    >
      {modalState?.target && (
        <DataFetchWrapper query={query}>
          {({
            data,
          }: {
            data: ResponseConfig<ResolveDatapointResponseCanonicalValueResponseDto>
          }) => (
            <DatapointResponseVersionsContent
              responseData={data.data}
              target={modalState.target}
              currentOrganizationId={user?.organization?.id}
              currentOrganizationName={user?.organization?.name}
              t={t}
            />
          )}
        </DataFetchWrapper>
      )}
    </Modal>
  )
}
